import React, { useState, useEffect } from 'react';
import { apiCall, getUser } from '../lib/api.js';
import EvaluationDetail from '../components/EvaluationDetail.jsx';
import { API_URL } from '../lib/config.js';
import { CATEGORIES } from '../lib/scorecard-config.js';

const ATTR_MAP = {};
CATEGORIES.forEach(cat => cat.attributes.forEach(a => { ATTR_MAP[a.id] = { label: a.label, weight: a.weight, category: cat.name }; }));

const PILL = {
  'Meets': 'bg-emerald-50 text-emerald-700 border-emerald-300',
  'Partial': 'bg-amber-50 text-amber-700 border-amber-300',
  'Misses': 'bg-red-50 text-red-700 border-red-300',
  'N/A': 'bg-gray-100 text-gray-500 border-gray-200',
};

const BAND_STYLES = {
  'Excellent': 'bg-emerald-50 text-emerald-700',
  'Good': 'bg-blue-50 text-blue-700',
  'Okay': 'bg-amber-50 text-amber-700',
  'Needs improvement': 'bg-orange-50 text-orange-700',
  'Failed': 'bg-red-50 text-red-700',
};

export default function MyScorecardsPage() {
  const user = getUser();
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAppeal, setShowAppeal] = useState(false);
  const [appealItems, setAppealItems] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiCall('/my/evaluations')
      .then(d => setEvals(d.evaluations || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const toggleAppealItem = (id) => {
    setAppealItems(prev => {
      const copy = { ...prev };
      if (copy[id]) delete copy[id];
      else copy[id] = { reason: '' };
      return copy;
    });
  };

  const handleSubmitAppeal = async (ev) => {
    const selected_items = Object.entries(appealItems).filter(([, v]) => v.reason.trim());
    if (!selected_items.length) { alert('Select at least one attribute and provide a reason.'); return; }
    setSubmitting(true);
    try {
      await apiCall('/appeal/submit', { method: 'POST', body: {
        evaluation_id: ev.id,
        agent_name: ev.agent_name,
        agent_email: user?.email,
        ticket_number: ev.ticket_number,
        ticket_subject: ev.ticket_subject,
        original_score: ev.final_score,
        rating_band: ev.rating_band,
        appealed_attributes: selected_items.map(([id, v]) => ({
          id, current_rating: ev.scores?.[id] || 'Unknown', reason: v.reason.trim(),
        })),
      }});
      alert('Appeal submitted! Your QA analyst has been notified.');
      setShowAppeal(false); setAppealItems({}); setSelected(null);
    } catch (e) { alert('Failed: ' + e.message); }
    setSubmitting(false);
  };

  if (selected) {
    const scores = selected.scores || {};
    const appealableAttrs = [];
    CATEGORIES.forEach(cat => {
      cat.attributes.forEach(a => {
        const rating = scores[a.id];
        if (rating === 'Partial' || rating === 'Misses') {
          appealableAttrs.push({ ...a, rating, category: cat.name });
        }
      });
    });

    return (
      <div>
        <EvaluationDetail evaluation={selected} onBack={() => { setSelected(null); setShowAppeal(false); setAppealItems({}); }} />

        {/* Appeal section */}
        {!showAppeal ? (
          (appealableAttrs.length > 0 || (selected.deductions && selected.deductions.length > 0)) && (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 mb-3">Disagree with this score?</p>
              <button onClick={() => setShowAppeal(true)}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium inline-flex items-center gap-2">
                <i className="ti ti-message-circle" aria-hidden="true"></i> Submit an appeal
              </button>
            </div>
          )
        ) : (
          <div className="bg-white rounded-xl border border-purple-200 p-5 mt-4">
            <h3 className="text-base font-medium mb-1">Submit an appeal</h3>
            <p className="text-xs text-gray-500 mb-4">Select the items you disagree with and explain why.</p>
            <div className="space-y-3 mb-4">
              {/* Appealable deductions */}
              {(selected.deductions || []).map((ded, i) => {
                const dedLabels = {
                  PD1: 'Missing required tag (e.g., Sabrent - TS / Sabrent - CS)',
                  PD2: 'Did not follow the documented process / ticket status',
                  PD3: 'Missing critical documentation or notes',
                };
                const dedId = 'DED_' + ded;
                return (
                  <div key={dedId} className={'rounded-lg border p-3 transition cursor-pointer ' + (appealItems[dedId] ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300')}
                    onClick={() => toggleAppealItem(dedId)}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={!!appealItems[dedId]} onChange={() => {}}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-800">{dedLabels[ded] || ded}</div>
                        <div className="text-[11px] text-gray-400 mt-0.5">Process deduction (-5%)</div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-medium border bg-amber-50 text-amber-700 border-amber-300">-5%</span>
                    </div>
                    {appealItems[dedId] && (
                      <div className="mt-3" onClick={e => e.stopPropagation()}>
                        <textarea value={appealItems[dedId]?.reason || ''} onChange={e => setAppealItems(p => ({ ...p, [dedId]: { reason: e.target.value } }))}
                          rows={2} placeholder="Why should this deduction be removed?"
                          className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400 resize-none" />
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Appealable attributes */}
              {appealableAttrs.map(attr => (
                <div key={attr.id} className={'rounded-lg border p-3 transition cursor-pointer ' + (appealItems[attr.id] ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300')}
                  onClick={() => toggleAppealItem(attr.id)}>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={!!appealItems[attr.id]} onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">{attr.label}</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{attr.category}</div>
                    </div>
                    <span className={'px-2.5 py-1 rounded-full text-[11px] font-medium border ' + (PILL[attr.rating] || '')}>{attr.rating}</span>
                  </div>
                  {appealItems[attr.id] && (
                    <div className="mt-3" onClick={e => e.stopPropagation()}>
                      <textarea value={appealItems[attr.id]?.reason || ''} onChange={e => setAppealItems(p => ({ ...p, [attr.id]: { reason: e.target.value } }))}
                        rows={2} placeholder="Why should this rating be changed?"
                        className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400 resize-none" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleSubmitAppeal(selected)} disabled={submitting}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 text-sm font-medium">
                {submitting ? 'Submitting...' : 'Submit appeal'}
              </button>
              <button onClick={() => { setShowAppeal(false); setAppealItems({}); }}
                className="px-5 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium">Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-medium mb-5">My scorecards</h2>
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : evals.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <i className="ti ti-clipboard text-3xl text-gray-300" aria-hidden="true"></i>
          <p className="text-gray-400 mt-2 text-sm">No scorecards yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {evals.map(ev => (
            <button key={ev.id} onClick={() => setSelected(ev)}
              className="w-full text-left bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-4 hover:border-purple-200 hover:shadow-sm transition cursor-pointer">
              <div className={'text-lg font-medium w-14 text-center ' + (ev.autofail_triggered ? 'text-red-500' : 'text-gray-900')}>
                {Math.round((ev.final_score || 0) * 100)}%
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">#{ev.ticket_number} <span className="font-normal text-gray-500">{ev.ticket_subject}</span></div>
                <div className="text-xs text-gray-400 mt-0.5">{ev.eval_date}</div>
              </div>
              <span className={'px-2.5 py-1 rounded-full text-xs font-medium ' + (BAND_STYLES[ev.rating_band] || '')}>{ev.rating_band}</span>
              <i className="ti ti-chevron-right text-gray-300" aria-hidden="true"></i>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
